import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  MessageSquare,
  Calendar,
  User,
  AlertCircle,
  Search,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "./DashboardLayout/DasboardLayout";
import { API_URL } from "./config";

const ChartDashboard = () => {
  const [charts, setCharts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");
  const pic = "https://srv547457.hstgr.cloud:3020";
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCharts = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/customer_admin_chats/admin-charts`
        );
        setCharts(response.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCharts();
  }, []);

  const filteredCharts = charts.filter((chart) => {
    const matchesSearch =
      chart.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      chart.user_surname.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter =
      filter === "all" ||
      (filter === "drivers" && chart.account_type === "driver") ||
      (filter === "customers" && chart.account_type === "customer");
    return matchesSearch && matchesFilter;
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10,
      },
    },
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="min-h-screen flex items-center justify-center bg-white">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-[#ffc000] border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
            <p className="text-sm text-gray-800">Loading dashboard...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="min-h-screen flex items-center justify-center bg-white">
          <div className="text-center p-4 backdrop-blur-lg bg-white rounded-xl shadow-2xl">
            <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-2" />
            <p className="text-lg text-red-400 font-semibold">
              Error fetching charts
            </p>
            <p className="text-gray-800 mt-1 text-sm">{error}</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-white p-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              className="h-1 w-20 bg-[#ffc000] mx-auto rounded-full"
            />
          </div>

          <div className="mb-6 space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name..."
                className="w-full pl-10 pr-3 py-2 bg-gray-100 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#ffc000] text-gray-800 placeholder-gray-600 text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              {["all", "drivers", "customers"].map((filterType) => (
                <motion.button
                  key={filterType}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setFilter(filterType)}
                  className={`flex-1 py-2 rounded-xl backdrop-blur-lg transition-all duration-300 ${
                    filter === filterType
                      ? "bg-[#ffc000] text-gray-900 shadow-lg shadow-[#ffc000]/20"
                      : "bg-gray-200 text-gray-800 border border-gray-300"
                  } text-sm`}
                >
                  {filterType.charAt(0).toUpperCase() + filterType.slice(1)}
                </motion.button>
              ))}
            </div>
          </div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
          >
            {filteredCharts.map((chart, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                onClick={() => navigate(`/ChartScreen`)} // Navigate on click
                className="bg-white rounded-xl overflow-hidden border border-gray-300 shadow-md cursor-pointer" // Added cursor-pointer
              >
                <div className="p-2 flex items-center space-x-3">
                  <div className="relative">
                    <img
                      src={`${pic}/${chart.profile}`}
                      alt={`${chart.user_name} ${chart.user_surname}`}
                      className="w-20 h-20 object-cover rounded-lg shadow-md"
                      onError={(e) => {
                        e.target.src = `https://ui-avatars.com/api/?name=${chart.user_name}+${chart.user_surname}&background=ffc000&color=fff`;
                      }}
                    />
                    <div className="absolute -bottom-1 -right-1 bg-[#ffc000] text-xs font-bold px-1 py-0.5 rounded-lg">
                      {chart.account_type}
                    </div>
                  </div>
                  <div className="flex-1 space-y-1">
                    <h2 className="text-lg font-semibold text-gray-800">
                      {chart.user_name} {chart.user_surname}
                    </h2>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center text-gray-600">
                        <User className="w-4 h-4 text-[#ffc000] mr-1" />
                        <span>ID: {chart.userid}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <MessageSquare className="w-4 h-4 text-[#ffc000] mr-1" />
                        <span className="truncate">{chart.last_message}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <Calendar className="w-4 h-4 text-[#ffc000] mr-1" />
                        <span>
                          {new Date(chart.last_message_date).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ChartDashboard;
